import { useForm, useFieldArray } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { UpdateCompanyDto } from '@monorepo/shared';
import {
  useGetCompany,
  useUpdateCompany,
  useRequestSirenUpdate,
} from '../services/company.service';
import { SirenUpdateModal } from '../components/company/SirenUpdateModal';
import {
  EditCompanyFormView,
  type EditCompanyFormData,
} from '../components/company/EditCompanyFormView';

export const EditCompanyPage = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSirenModalOpen, setIsSirenModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { data: company, isLoading, isError } = useGetCompany(companyId || '');
  const updateCompanyMutation = useUpdateCompany(companyId || '');
  const requestSirenUpdateMutation = useRequestSirenUpdate(companyId || '');

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<EditCompanyFormData>({
    defaultValues: {
      locations: [],
      dataProtectionSettings: {
        hasDpoDeclaration: false,
        hasDataProtectionRepresentative: false,
        isExternalConsultant: false,
      },
      hasVideoSurveillance: false,
      hasVideoProtection: false,
      recordsPhoneCalls: false,
      hasWorksCouncil: false,
      hasLegalDepartment: false,
      hasItDepartment: false,
    },
  });

  const locationsFieldArray = useFieldArray({
    control,
    name: 'locations',
  });

  const hasDpoDeclaration = watch('dataProtectionSettings.hasDpoDeclaration');
  const hasDataProtectionRepresentative = watch(
    'dataProtectionSettings.hasDataProtectionRepresentative',
  );
  const isExternalConsultant = watch('dataProtectionSettings.isExternalConsultant');
  const changeMainContact = watch('changeMainContact');
  const industrySector = watch('industrySector');

  useEffect(() => {
    if (company) {
      const mainLocation = company.locations?.find((l) => l.isMainCompanyLocation);
      const otherLocations = company.locations?.filter((l) => !l.isMainCompanyLocation) || [];

      reset({
        legalName: company.legalName,
        industrySector: company.industrySector,
        ...(company.industrySectorOther
          ? { industrySectorOther: company.industrySectorOther }
          : {}),
        headcountRange: company.headcountRange,
        mainWebsiteUrl: company.mainWebsiteUrl ?? '',
        headOfficeAddress: company.headOfficeAddress,
        dataProtectionSettings: company.dataProtectionSettings,
        hasVideoSurveillance: company.hasVideoSurveillance,
        hasVideoProtection: company.hasVideoProtection,
        recordsPhoneCalls: company.recordsPhoneCalls,
        hasWorksCouncil: company.hasWorksCouncil,
        hasLegalDepartment: company.hasLegalDepartment,
        hasItDepartment: company.hasItDepartment,
        mainLocation: mainLocation || {
          siret: '',
          address: {
            street: '',
            postalCode: '',
            city: '',
            country: '',
          },
        },
        locations: otherLocations,
      });
    }
  }, [company, reset]);

  const onSubmit = async (data: EditCompanyFormData) => {
    try {
      const { mainLocation, ...rest } = data;
      const submitData: UpdateCompanyDto = {
        ...rest,
        locations: [{ ...mainLocation, isMainCompanyLocation: true }, ...(data.locations || [])],
      };
      await updateCompanyMutation.mutateAsync(submitData);
      navigate('/');
    } catch (error) {
      console.error('Company update failed:', error);
    }
  };

  const handleSirenUpdateRequest = async (correctedSiren: string) => {
    try {
      await requestSirenUpdateMutation.mutateAsync({
        oldSiren: company?.siren || '',
        newSiren: correctedSiren,
        companyName: company?.legalName || '',
      });
      setSuccessMessage(t('company.siren_update_modal.success'));
      setIsSirenModalOpen(false);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('SIREN update request failed:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-secondary">{t('common.loading')}</div>
      </div>
    );
  }

  if (isError || !company) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{t('common.error')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-3xl font-display text-primary mb-2">{t('company.settings.title')}</h1>
          <p className="text-secondary">{t('company.settings.subtitle')}</p>
        </div>

        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto p-6">
          <EditCompanyFormView
            register={register}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
            locationsFieldArray={locationsFieldArray}
            locationsData={watch('locations') || []}
            company={company}
            industrySector={industrySector}
            hasDpoDeclaration={hasDpoDeclaration || false}
            hasDataProtectionRepresentative={hasDataProtectionRepresentative || false}
            isExternalConsultant={isExternalConsultant || false}
            changeMainContact={changeMainContact || false}
            successMessage={successMessage}
            isSubmitError={updateCompanyMutation.isError}
            isSubmitting={updateCompanyMutation.isPending}
            onSubmit={handleSubmit(onSubmit)}
            onRequestSirenUpdate={() => setIsSirenModalOpen(true)}
          />
        </div>
      </div>

      <SirenUpdateModal
        isOpen={isSirenModalOpen}
        onClose={() => setIsSirenModalOpen(false)}
        onSubmit={handleSirenUpdateRequest}
        currentSiren={company?.siren || ''}
        isLoading={requestSirenUpdateMutation.isPending}
      />
    </div>
  );
};
